import Picker from "@/components/picker/mobile-picker";
import { PickerDivider } from "@/components/picker/mobile-picker/picker-divider";
import SegmentedControl from "@/components/toggle/segmented-control";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useMemo, useState } from "react";

interface HeightSelectModalProps {
  defaultValue: string;
  defaultUnit: string;
  onSelect: (value: string, unit: "cm" | "inches") => void;
  getScrollBlockerProps?: () => {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onWheel: (e: React.WheelEvent) => void;
  };
}

const heightRanges = {
  cm: { start: 120, end: 220 },
  inches: { start: 47, end: 87 },
} as const;

const clampHeight = (val: number, unit: "cm" | "inches") => {
  const range = heightRanges[unit];
  return Math.min(Math.max(val, range.start), range.end);
};

const convertHeight = (value: string, from: "cm" | "inches", to: "cm" | "inches") => {
  const numeric = parseFloat(value);
  if (Number.isNaN(numeric)) return "";
  if (from === to) return numeric.toString();
  const converted = from === "cm" ? numeric * 0.393701 : numeric / 0.393701;
  return Math.round(converted).toString();
};

export const HeightSelectModal = memo(function HeightSelectModal({
  defaultValue,
  defaultUnit,
  onSelect,
}: HeightSelectModalProps) {
  const [unit, setUnit] = useState<"cm" | "inches">((defaultUnit as "cm" | "inches") ?? "cm");
  const [height, setHeight] = useState<string>(defaultValue);

  const options = useMemo(() => {
    const range = heightRanges[unit];
    return Array.from({ length: range.end - range.start + 1 }, (_, idx) => {
      const val = (range.start + idx).toString();
      return { value: val, name: val };
    });
  }, [unit]);

  const pickerValue = useMemo(
    () => ({
      height: height || options[0]?.value || "0",
    }),
    [height, options],
  );

  return (
    <div className="space-y-3 p-9">
      <h3 className={textVariants.title3 + " text-center"}>Your Height</h3>

      <SegmentedControl
        options={[
          { value: "cm", label: "cm" },
          { value: "inches", label: "Inches" },
        ]}
        value={unit}
        onChange={(val) => {
          const nextUnit = val as "cm" | "inches";
          const converted = convertHeight(height, unit, nextUnit);
          const clamped = converted
            ? clampHeight(parseFloat(converted), nextUnit).toString()
            : converted;
          setUnit(nextUnit);
          if (clamped) {
            setHeight(clamped);
          }
        }}
      />

      <div className="relative">
        <PickerDivider itemHeight={64} />
        <Picker
          value={pickerValue}
          onChange={(val) => setHeight(val.height as string)}
          wheelMode="natural"
          enableVelocity={true}
          itemHeight={64}
          className="mask-image-none"
          showDivider={false}
          height={64 * 3}
        >
          <Picker.Column name="height">
            {options.map((option) => (
              <Picker.Item key={option.value} value={option.value}>
                {({ selected }) => (
                  <div
                    className={cn("flex h-16 items-center justify-center text-center opacity-50", {
                      [textVariants.title1]: !selected,
                      [textVariants.largeTitle]: selected,
                      "font-semibold text-white opacity-100": selected,
                    })}
                  >
                    {option.name}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>
      </div>

      <FilledButton
        color="black"
        text="Confirm Height"
        onClick={() => {
          const val = pickerValue.height || options[0]?.value || "";
          onSelect(val, unit);
        }}
      />
    </div>
  );
});
